import { useState, useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from "@common-reducers/hooks/store.hook";
import { 
  fetchCreditUsage, 
  selectCreditsData, 
  selectIsLoading as selectCreditUsageIsLoading,
  selectError
} from '@common-reducers/CreditUsageReducer';

// Helper function for safe data access
const getValueOrDefault = (data: any, path: string, defaultValue: any) => {
  if (!data) return defaultValue;
  
  const keys = path.split('.');
  let value = data;
  
  for (const key of keys) {
    if (value === undefined || value === null) return defaultValue;
    value = value[key];
  }
  
  return value === undefined || value === null ? defaultValue : value;
};

export interface CreditStatus {
  creditsRemaining: number;
  percentageUsed: number;
  totalCredits: number;
  isOutOfCredits: boolean;
  isLowCredits: boolean;
  daysRemaining: number;
  renewalDate: string;
}

/** The credit status from the usage response. #357: the credit add-on is a hard allowance, so nothing bypasses it. */
export function creditStatusFrom(creditsData: unknown): CreditStatus {
  return {
    creditsRemaining: getValueOrDefault(creditsData, 'creditsRemaining', 0),
    percentageUsed: getValueOrDefault(creditsData, 'percentageUsed', 0),
    totalCredits: getValueOrDefault(creditsData, 'totalCredits', 0),
    isOutOfCredits: getValueOrDefault(creditsData, 'creditsRemaining', 0) <= 0,
    isLowCredits: getValueOrDefault(creditsData, 'percentageUsed', 0) > 80 && getValueOrDefault(creditsData, 'creditsRemaining', 0) > 0,
    daysRemaining: getValueOrDefault(creditsData, 'daysRemaining', 0),
    renewalDate: getValueOrDefault(creditsData, 'renewalDate', 'Unknown')
  };
}

export interface CreditManagementHook {
  // Credit status
  creditStatus: CreditStatus;
  isLoading: boolean;
  error: string | null;
  
  // Modal management
  isAddCreditsModalOpen: boolean;
  openAddCreditsModal: () => void;
  closeAddCreditsModal: () => void;
  
  // Actions
  refreshCreditData: (timeframe?: string) => void;
  
  // Computed values
  shouldShowWarning: boolean;
  warningLevel: 'none' | 'low' | 'critical';
}

/**
 * Custom hook for managing credit-related operations
 * Provides a centralized way to handle credit data, modals, and actions
 */
export const useCreditManagement = (
  autoFetch: boolean = true,
  timeframe: string = 'billing_period'
): CreditManagementHook => {
  const dispatch = useAppDispatch();
  const [isAddCreditsModalOpen, setIsAddCreditsModalOpen] = useState(false);
  
  // Redux selectors
  const creditsData = useAppSelector(selectCreditsData);
  const isLoading = useAppSelector(selectCreditUsageIsLoading);
  const error = useAppSelector(selectError);
  
  // Fetch credit data on mount if autoFetch is enabled
  useEffect(() => {
    if (autoFetch) {
      dispatch(fetchCreditUsage(timeframe));
    }
  }, [dispatch, autoFetch, timeframe]);
  
  const creditStatus = creditStatusFrom(creditsData);
  
  // Modal management functions
  const openAddCreditsModal = useCallback(() => {
    setIsAddCreditsModalOpen(true);
  }, []);
  
  const closeAddCreditsModal = useCallback(() => {
    setIsAddCreditsModalOpen(false);
  }, []);
  
  // Refresh credit data
  const refreshCreditData = useCallback((refreshTimeframe: string = timeframe) => {
    dispatch(fetchCreditUsage(refreshTimeframe));
  }, [dispatch, timeframe]);
  
  // Computed values
  const shouldShowWarning = creditStatus.isOutOfCredits || creditStatus.isLowCredits;
  const warningLevel: 'none' | 'low' | 'critical' = creditStatus.isOutOfCredits 
    ? 'critical' 
    : creditStatus.isLowCredits 
      ? 'low' 
      : 'none';
  
  return {
    creditStatus,
    isLoading,
    error,
    isAddCreditsModalOpen,
    openAddCreditsModal,
    closeAddCreditsModal,
    refreshCreditData,
    shouldShowWarning,
    warningLevel
  };
}; 